import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Select,
  MenuItem,
  Paper,
  Button
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";

/* ================= OPTIONS ================= */

const operatorOptions = [
  "None",
  ">", "<", "=", "+", "-", "*", "/", "%", "(", ")", "MAX", "MIN", "AVG", "LEFT", "RIGHT", "TRIM"
];

const valueTypeOptions = [
  "None",
  ...[
    "Constant",
    "Variable",
    "Attributes",
    "Date",
    "Source",
    "Sequence Number",
    "Currency"
  ].sort((a, b) => a.localeCompare(b))
];

const logicalOperators = [
  "None",
  "AND",
  "OR",
  ")"
];

const characterEnabledOperators = ["LEFT", "RIGHT", "TRIM", "MIN", "MAX", "AVG", "%"];
/* ================= HELPERS ================= */

const createEmptyRow = (rowNo) => ({
  rowNo,
  operator: "",
  characters: "",
  type: "",
  value: "",
  logicalOperator: ""
});

const isRowComplete = (row) =>
  row.operator && row.type && row.value;

/* ================= COMPONENT ================= */

const Rules = () => {
  const [ruleHeader, setRuleHeader] = useState({
    name: "",
    startDate: "",
    endDate: "",
    active: true
  });

  const [rows, setRows] = useState([createEmptyRow(1)]);
  const location = useLocation();
  const navigate = useNavigate();

  const { ruleData, isEditMode } = location.state || {};
  const isViewMode = ruleData && isEditMode === false;

  /* ================= HEADER ================= */

  const handleHeaderChange = (key, value) => {
    setRuleHeader((prev) => ({ ...prev, [key]: value }));
  };


  useEffect(() => {
    if (ruleData) {
      setRuleHeader({
        name: ruleData.name || "",
        startDate: ruleData.startDate || "",
        endDate: ruleData.endDate || "",
        active: ruleData.status === "Active"
      });

      if (ruleData.adapterRuleConditions?.length) {
        const mappedRows = ruleData.adapterRuleConditions.map((cond, index) => ({
          rowNo: index + 1,
          operator: cond.attributeOperator || "",
          characters:
            cond.characters && cond.characters !== "None"
              ? cond.characters
              : "",
          type: cond.attributeType || "",
          value: cond.attributeValue || "",
          logicalOperator:
            cond.attributeCondition === "None"
              ? ""
              : cond.attributeCondition
        }));

        setRows(mappedRows);
      }
    }
  }, [ruleData]);

  /* ================= ROW CHANGE ================= */

  const handleRowChange = (idx, key, value) => {
    setRows((prev) => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [key]: value };
      //If operator changes and not LEFT/RIGHT/TRIM → clear characters
      if (
        key === "operator" &&
        !characterEnabledOperators.includes(value)
      ) {
        updated[idx].characters = "";
      }


      // ✅ Auto add row AFTER VALUE entry
      if (
        key === "value" &&
        isRowComplete(updated[idx]) &&
        idx === updated.length - 1
      ) {
        updated.push(createEmptyRow(updated.length + 1));
      }

      return updated;
    });
  };

  /* ================= SAVE ================= */

  const buildRuleExpression = (rows) => {
    let currentVariable = "";

    return rows
      .map((row) => {
        let expr = "";

        // 1️⃣ Opening bracket + variable → STORE ONLY
        if (row.operator === "(") {
          if (row.type === "Variable" && row.value) {
            currentVariable = row.value;
            return "(";
          }
          return "(";
        }

        // 2️⃣ Normal condition row
        if (row.operator && row.value) {
          if (currentVariable) {
            expr = `${currentVariable} ${row.operator} ${row.value}`;
          } else {
            expr = `${row.operator} ${row.value}`;
          }
        }

        // 3️⃣ Logical operator / closing bracket
        if (row.logicalOperator === ")") {
          expr += " )";
        } else if (row.logicalOperator) {
          expr += ` ${row.logicalOperator}`;
        }

        return expr.trim();
      })
      .filter(Boolean)
      .join(" ");
  };

  const handleSave = async () => {
    const adapterRuleConditions = buildRuleConditions(rows);

    if (!ruleHeader.name.trim()) {
      alert("Rule name is required");
      return;
    }

    if (adapterRuleConditions.length === 0) {
      alert("At least one rule condition is required");
      return;
    }

    const requestBody = {
      name: ruleHeader.name,
      type: "CUSTOM",
      orgId: "1",
      createdBy: 1,
      status: ruleHeader.active ? "Active" : "Inactive",
      adapterRuleConditions
    };

    console.log("📦 REQUEST BODY:", requestBody);

    try {
      const response = await fetch(
        "https://apiservices.iepa.in:8196/adapters/rules/create",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify(requestBody)
        }
      );

      if (!response.ok) {
        throw new Error(await response.text());
      }

      const data = await response.json();
      console.log("✅ RULE CREATED:", data);

      alert("Rule created successfully");

      // optional reset
      handleCancel();

    } catch (err) {
      console.error("❌ RULE CREATE FAILED:", err);
      alert(err.message || "Failed to create rule");
    }
  };

  /* ================= CANCEL ================= */

  const handleCancel = () => {
    setRuleHeader({
      name: "",
      startDate: "",
      endDate: "",
      active: true
    });
    setRows([createEmptyRow(1)]);
    navigate("/RulesList");
  };

  const buildRuleConditions = (rows) => {
    return rows
      .filter(
        (r) =>
          r.operator &&
          r.type &&
          r.value
      )
      .map((r) => ({
        attributeOperator: r.operator,
        characters: r.characters ? r.characters : "None",
        attributeType: r.type,
        attributeValue: r.value,
        attributeCondition: r.logicalOperator || "None"
      }));
  };

  /* ================= UI ================= */

  return (
    <Box>
      {/* ===== HEADER ===== */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6">Rule Details</Typography>

        <Grid container spacing={2} mt={1}>
          <Grid item xs={4}>
            <TextField
              fullWidth
              required
              label="Name of Rule"
              value={ruleHeader.name}
              disabled={isViewMode}
              onChange={(e) =>
                handleHeaderChange("name", e.target.value)
              }
            />
          </Grid>

          <Grid item xs={2}>
            <TextField
              type="date"
              label="Start Date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={ruleHeader.startDate}
              disabled={isViewMode}
              onChange={(e) =>
                handleHeaderChange("startDate", e.target.value)
              }
            />
          </Grid>

          <Grid item xs={2}>
            <TextField
              type="date"
              label="End Date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={ruleHeader.endDate}
              disabled={isViewMode}
              onChange={(e) =>
                handleHeaderChange("endDate", e.target.value)
              }
            />
          </Grid>

          <Grid item xs={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={ruleHeader.active}
                  onChange={(e) =>
                    handleHeaderChange("active", e.target.checked)
                  }
                  color="success"
                  disabled={isViewMode}
                />
              }
              label="Active"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* ===== RULE GRID ===== */}
      <Paper sx={{ p: 3 }} >
        <Typography variant="h6">Rule Conditions</Typography>

        <Table disabled={isViewMode}>
          <TableHead>
            <TableRow>
              <TableCell>Row</TableCell>
              <TableCell>Operator</TableCell>
              <TableCell>Characters</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Value</TableCell>
              <TableCell>Condition</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((row, idx) => (
              <TableRow key={idx}>
                <TableCell>{row.rowNo}</TableCell>

                <TableCell>
                  <Select
                    fullWidth
                    disabled={isViewMode}
                    value={row.operator}
                    onChange={(e) =>
                      handleRowChange(idx, "operator", e.target.value)
                    }
                  >
                    {operatorOptions.map((op) => (
                      <MenuItem key={op} value={op}>
                        {op}
                      </MenuItem>
                    ))}
                  </Select>
                </TableCell>

                <TableCell>
                  <TextField
                    fullWidth
                    value={row.characters}
                    placeholder="None"
                    disabled={
                      isViewMode ||
                      !characterEnabledOperators.includes(row.operator)
                    }
                    inputProps={{
                      inputMode: "numeric",
                      pattern: "[0-9]*"
                    }}
                    onChange={(e) => {
                      const value = e.target.value;

                      // ✅ Allow only numbers
                      if (/^\d*$/.test(value)) {
                        handleRowChange(idx, "characters", value);
                      }
                    }}
                  />
                </TableCell>

                <TableCell>
                  <Select
                    fullWidth
                    disabled={isViewMode}
                    value={row.type}
                    onChange={(e) =>
                      handleRowChange(idx, "type", e.target.value)
                    }
                  >
                    {valueTypeOptions.map((v) => (
                      <MenuItem key={v} value={v}>
                        {v}
                      </MenuItem>
                    ))}
                  </Select>
                </TableCell>

                <TableCell>
                  <TextField
                    fullWidth
                    disabled={isViewMode}
                    value={row.value}
                    onChange={(e) =>
                      handleRowChange(idx, "value", e.target.value)
                    }
                  />
                </TableCell>

                {/* ✅ CONDITION AVAILABLE FOR ALL ROWS */}
                <TableCell>
                  <Select
                    fullWidth
                    disabled={isViewMode}
                    value={row.logicalOperator}
                    onChange={(e) =>
                      handleRowChange(
                        idx,
                        "logicalOperator",
                        e.target.value
                      )
                    }
                    displayEmpty
                  >
                    <MenuItem value="">None</MenuItem>
                    {logicalOperators.map((op) => (
                      <MenuItem key={op} value={op}>
                        {op}
                      </MenuItem>
                    ))}
                  </Select>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* ===== ACTIONS ===== */}
        <Box mt={3} display="flex" gap={2}>
          <Button variant="contained" onClick={handleSave}>
            Save
          </Button>

          <Button
            variant="outlined"
            sx={{
              backgroundColor: "#fff",
              color: "#000",
              borderColor: "#000"
            }}
            onClick={handleCancel}
          >
            Cancel
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default Rules;
